/**
 * OverviewTab Component
 *
 * Displays entity overview: name, icon, access permissions, and configuration.
 */

import React from 'react'
import { Descriptions, Typography, Tag, Space, Card, Empty, Tooltip } from 'antd'
import {
  InfoCircleOutlined,
  LockOutlined,
  FileTextOutlined,
  AppstoreOutlined,
  SearchOutlined,
  TagOutlined
} from '@ant-design/icons'
import { getIconFromName } from '../utils'
import type { TabProps } from '../types'

const { Text, Paragraph, Title } = Typography

/**
 * Format a preset filter for human-readable display
 * e.g., { constraint_type: "EQUAL", value: "active" } -> 'status = "active"'
 */
function formatPresetFilter(field: string, filter: { constraint_type: string; value?: unknown; values?: unknown[] } | null): string {
  if (!filter) {
    return '(no filter - show all)'
  }

  const { constraint_type, value, values } = filter

  switch (constraint_type) {
    case 'EQUAL':
      return `${field} = ${formatValue(value)}`
    case 'NOT_EQUAL':
      return `${field} != ${formatValue(value)}`
    case 'IS_NULL':
      return `${field} IS NULL`
    case 'IS_NOT_NULL':
      return `${field} IS NOT NULL`
    case 'IN':
      return `${field} IN (${(values || []).map(formatValue).join(', ')})`
    case 'NOT_IN':
      return `${field} NOT IN (${(values || []).map(formatValue).join(', ')})`
    case 'CONTAINS':
      return `${field} CONTAINS ${formatValue(value)}`
    case 'GT':
      return `${field} > ${formatValue(value)}`
    case 'GTE':
    case 'GE':
      return `${field} >= ${formatValue(value)}`
    case 'LT':
      return `${field} < ${formatValue(value)}`
    case 'LTE':
    case 'LE':
      return `${field} <= ${formatValue(value)}`
    default:
      return `${field} ${constraint_type} ${formatValue(value)}`
  }
}

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return 'NULL'
  if (typeof val === 'string') return `"${val}"`
  return String(val)
}

export function OverviewTab({ metadata }: TabProps): React.ReactElement {
  const display = metadata.display
  const access = metadata.access

  if (!display && !access) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No display metadata available"
      />
    )
  }

  return (
    <div style={{ padding: '8px 0' }}>
      {/* Header with icon and name */}
      {display && (
        <Card size="small" style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <Tooltip title={display.icon || 'Default icon'}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '8px',
                background: '#f0f5ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #d6e4ff',
                fontSize: '24px',
                color: '#1890ff'
              }}>
                {getIconFromName(display.icon)}
              </div>
            </Tooltip>
            <div style={{ flex: 1 }}>
              <Title level={5} style={{ margin: 0 }}>
                {display.label_singular || 'Unnamed Entity'}
              </Title>
              <Text type="secondary">
                Plural: {display.label || (display.label_singular ? display.label_singular + 's' : 'Unnamed')}
              </Text>
              {display.description && (
                <Paragraph
                  type="secondary"
                  style={{ marginTop: '8px', marginBottom: 0 }}
                >
                  {display.description}
                </Paragraph>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Main details */}
      <Descriptions
        bordered
        column={1}
        size="small"
        labelStyle={{ width: '140px', background: '#fafafa' }}
      >
        {display && (
          <>
            <Descriptions.Item
              label={
                <Space>
                  <TagOutlined />
                  <span>Labels</span>
                </Space>
              }
            >
              <Text strong>
                {display.label_singular || '-'} / {display.label || '-'}
              </Text>
            </Descriptions.Item>

            <Descriptions.Item
              label={
                <Space>
                  <AppstoreOutlined />
                  <span>Icon</span>
                </Space>
              }
            >
              <Space>
                <span style={{ fontSize: '16px' }}>{getIconFromName(display.icon)}</span>
                <Text code style={{ fontSize: '11px' }}>{display.icon || 'default'}</Text>
              </Space>
            </Descriptions.Item>

            {display.empty_message && (
              <Descriptions.Item
                label={
                  <Space>
                    <InfoCircleOutlined />
                    <span>Empty Message</span>
                  </Space>
                }
              >
                <Text type="secondary">{display.empty_message}</Text>
              </Descriptions.Item>
            )}
          </>
        )}

        {access && (
          <>
            <Descriptions.Item
              label={
                <Space>
                  <LockOutlined />
                  <span>Read Access</span>
                </Space>
              }
            >
              {(access.readRolesTenant?.length || access.readRolesGlobal?.length) ? (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  {access.readRolesTenant && access.readRolesTenant.length > 0 && (
                    <div>
                      <Text type="secondary" style={{ fontSize: '11px' }}>Tenant: </Text>
                      <Text style={{ fontSize: '12px' }}>{access.readRolesTenant.join(', ')}</Text>
                    </div>
                  )}
                  {access.readRolesGlobal && access.readRolesGlobal.length > 0 && (
                    <div>
                      <Text type="secondary" style={{ fontSize: '11px' }}>Global: </Text>
                      <Text style={{ fontSize: '12px' }}>{access.readRolesGlobal.join(', ')}</Text>
                    </div>
                  )}
                </Space>
              ) : (
                <Text type="secondary">Not configured</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <Space>
                  <LockOutlined />
                  <span>Write Access</span>
                </Space>
              }
            >
              {(access.writeRolesTenant?.length || access.writeRolesGlobal?.length) ? (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  {access.writeRolesTenant && access.writeRolesTenant.length > 0 && (
                    <div>
                      <Text type="secondary" style={{ fontSize: '11px' }}>Tenant: </Text>
                      <Text style={{ fontSize: '12px' }}>{access.writeRolesTenant.join(', ')}</Text>
                    </div>
                  )}
                  {access.writeRolesGlobal && access.writeRolesGlobal.length > 0 && (
                    <div>
                      <Text type="secondary" style={{ fontSize: '11px' }}>Global: </Text>
                      <Text style={{ fontSize: '12px' }}>{access.writeRolesGlobal.join(', ')}</Text>
                    </div>
                  )}
                </Space>
              ) : (
                <Text type="secondary">Not configured</Text>
              )}
            </Descriptions.Item>
          </>
        )}
      </Descriptions>

      {/* Additional metadata sections */}
      {metadata.form_sections && metadata.form_sections.length > 0 && (
        <Card
          size="small"
          title={
            <Space>
              <FileTextOutlined />
              <span>Form Sections</span>
            </Space>
          }
          style={{ marginTop: '16px' }}
        >
          <ol style={{ margin: 0, paddingLeft: '20px' }}>
            {metadata.form_sections
              .sort((a, b) => a.order - b.order)
              .map((section) => (
                <li key={section.name} style={{ marginBottom: '4px' }}>
                  <Text>{section.name}</Text>
                  {section.description && (
                    <Text type="secondary" style={{ marginLeft: '8px', fontSize: '12px' }}>
                      - {section.description}
                    </Text>
                  )}
                </li>
              ))}
          </ol>
        </Card>
      )}

      {/* Presets */}
      {metadata.presets && (
        <Card
          size="small"
          title={
            <Space>
              <AppstoreOutlined />
              <span>Presets ({metadata.presets.field})</span>
            </Space>
          }
          style={{ marginTop: '16px' }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 500, fontSize: '12px', color: '#8c8c8c' }}>
                  Preset Name
                </th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 500, fontSize: '12px', color: '#8c8c8c' }}>
                  Filter
                </th>
              </tr>
            </thead>
            <tbody>
              {metadata.presets.options.map((option) => (
                <tr key={option.key} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '8px 12px' }}>
                    <Space>
                      <Text strong={option.key === metadata.presets?.default}>
                        {option.label}
                      </Text>
                      {option.key === metadata.presets?.default && (
                        <Tag color="blue" style={{ margin: 0, fontSize: '10px' }}>default</Tag>
                      )}
                    </Space>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Text code style={{ fontSize: '12px' }}>
                      {formatPresetFilter(metadata.presets!.field, option.filter)}
                    </Text>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Search box config */}
      {metadata.search !== undefined && (
        <Card
          size="small"
          title={
            <Space>
              <SearchOutlined />
              <span>Search Box Configuration</span>
            </Space>
          }
          style={{ marginTop: '16px' }}
        >
          {metadata.search === false ? (
            <Tag color="red">Search box disabled for this entity</Tag>
          ) : (
            <Space direction="vertical" size="small">
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Fields included when users type in the table search box:
              </Text>
              <div>
                {metadata.search.fields.map((field) => (
                  <Tag key={field} color="blue" style={{ margin: '0 4px 4px 0' }}>{field}</Tag>
                ))}
              </div>
              {metadata.search.placeholder && (
                <div>
                  <Text type="secondary">Placeholder text: </Text>
                  <Text code>{metadata.search.placeholder}</Text>
                </div>
              )}
            </Space>
          )}
        </Card>
      )}
    </div>
  )
}

export default OverviewTab
